/**
 * What remains of a filter from a link (design §5.5): known properties and operators, values
 * of the right shape, ids that are ids at all (a uuid, or "me"/"none" for the team) and that
 * still exist where the options are known; a source that has not loaded keeps its uuids until
 * it has. `dropped` counts each removed condition or value, for the one toast.
 */
import { normalizeFilter } from './codec'
import { isDayCount, isRealDay } from './dateRanges'
import type { Condition, Filter, OptionSource, PropertyDef, Register, ValueKind } from './types'

/** The ids that exist per source, once that source has loaded; a missing key means "not loaded yet". */
export type KnownIds = Partial<Record<Exclude<OptionSource, 'static'>, ReadonlySet<string>>>

// The limits of the filter model (backend `query/schema.py`, ED-34).
const MAX_QUICK = 20
const MAX_GROUPS = 10
const MAX_GROUP_CONDITIONS = 20
const MAX_VALUES = 100
const MAX_TEXT = 200

// A NUL byte, or a lone UTF-16 surrogate: JSON can carry both, the database cannot take either.
const LONE_SURROGATE = /[\ud800-\udfff]/u
const isStorable = (text: string): boolean => !text.includes('\u0000') && !LONE_SURROGATE.test(text)

function isNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

/** Whether `value` has the shape an operator of this kind needs (the server's `parse_value`). */
export function isValidValue(kind: ValueKind, value: unknown): boolean {
  switch (kind) {
    case 'none':
      return true
    case 'text':
      return typeof value === 'string' && value.trim() !== '' && value.length <= MAX_TEXT && isStorable(value)
    case 'list':
      return (
        Array.isArray(value) &&
        value.length >= 1 &&
        value.length <= MAX_VALUES &&
        value.every((item) => typeof item === 'string' && item.length <= MAX_TEXT)
      )
    case 'date':
      return isRealDay(value)
    case 'date_range':
      return Array.isArray(value) && value.length === 2 && isRealDay(value[0]) && isRealDay(value[1]) && value[0] <= value[1]
    case 'days':
      return isDayCount(value)
    case 'number':
      return isNumber(value)
    case 'number_range':
      return Array.isArray(value) && value.length === 2 && isNumber(value[0]) && isNumber(value[1]) && value[0] <= value[1]
  }
}

// The form the server writes. It reads a few more spellings, but only this one is ever in a link.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Whether `value` is a uuid. The server reads every id of a dynamic option source as one and
 * answers 422 when it is not: "Ongeldige waarde bij …" for a search, a validation error for the
 * whole call of `GET /refs`.
 */
export function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID.test(value)
}

/**
 * Whether an id of a list value can still be right. A static option must be one of the register's.
 * Any other id must be a uuid, whether or not its source has loaded (the server rejects the rest),
 * plus "me" and "none" for the team; once the source has loaded it must also be in it. Before that
 * a uuid stays, because we cannot tell yet.
 */
function idCheck(property: PropertyDef, known: KnownIds): (value: string) => boolean {
  const options = property.options
  if (options?.source === 'static') {
    const allowed = new Set(options.values.map((option) => option.value))
    return (value) => allowed.has(value)
  }
  const loaded = options ? known[options.source] : undefined
  const exists = (value: string): boolean => isUuid(value) && (loaded === undefined || loaded.has(value))
  return options?.source === 'users' ? (value) => value === 'me' || value === 'none' || exists(value) : exists
}

function sanitizeCondition(
  condition: Condition,
  register: Register,
  known: KnownIds,
): { condition: Condition | null; dropped: number } {
  const property = register.properties.find((candidate) => candidate.key === condition.property)
  const operator = property?.operators.find((candidate) => candidate.key === condition.operator)
  if (!property || !operator || !isValidValue(operator.value, condition.value)) return { condition: null, dropped: 1 }
  if (operator.value === 'none') return { condition: { property: condition.property, operator: condition.operator }, dropped: 0 }
  const exists = operator.value === 'list' ? idCheck(property, known) : null
  if (!exists) return { condition, dropped: 0 }
  const values = condition.value as string[]
  const remaining = values.filter(exists)
  const dropped = values.length - remaining.length
  if (remaining.length === 0) return { condition: null, dropped }
  return { condition: dropped === 0 ? condition : { ...condition, value: remaining }, dropped }
}

export function sanitizeFilter(
  filter: Filter,
  register: Register,
  known: KnownIds,
): { filter: Filter; dropped: number } {
  let dropped = 0
  const keep = (conditions: Condition[], limit: number): Condition[] => {
    dropped += Math.max(conditions.length - limit, 0)
    const kept: Condition[] = []
    for (const condition of conditions.slice(0, limit)) {
      const result = sanitizeCondition(condition, register, known)
      dropped += result.dropped
      if (result.condition) kept.push(result.condition)
    }
    return kept
  }
  const normal = normalizeFilter(filter)
  const quick = keep(normal.quick, MAX_QUICK)
  for (const group of normal.groups.slice(MAX_GROUPS)) dropped += group.length
  const groups = normal.groups.slice(0, MAX_GROUPS).map((group) => keep(group, MAX_GROUP_CONDITIONS))
  return { filter: normalizeFilter({ quick, groups }), dropped }
}
